<template>
  <div
    :style="$register && !isMoviePage ? '' : 'position: relative'"
    data-test-id="showtimes-list"
  >
    <q-scroll-observer
      v-if="$q.screen.xs && !$register && showShowtimesByFilm"
      @scroll="scrollHandler"
    />
    <div
      v-if="moviesAndShowingsByDate"
      data-test-id="showtimes-list"
      :class="($register && !useMobileRegisterLayout) && !isMoviePage ? 'absolute-full row items-start' : ''"
    >
      <section-showtimes-heading
        v-if="!isMoviePage && $consumer"
        :title="title"
        :show-title="showTitle"
        :heading-color="headingColor"
        :heading-color-hex="headingColorHex"
        :heading-align="headingAlign"
        :heading-font-size="headingFontSize"
        :heading-type="headingType"
        :heading-background-image-url="headingBackgroundImageUrl"
        :heading-background-image-repeat="headingBackgroundImageRepeat"
        :show-date-filter="showDateFilter"
        :showtimes-site-selector-types="showtimesSiteSelectorTypes"
        :flatten-zone-groups="flattenZoneGroups"
        :showtimes-date-picker-type="showtimesDatePickerType"
        :showtimes-display-type-allow-toggle="showtimesDisplayTypeAllowToggle"
        :showtimes-display="showtimesDisplay"
        :showtimes-allow-all="showtimesAllowAll"
        :show-multiple-sites="showMultipleSites"
        :use-current-location="useCurrentLocation"
        :date="date"
        :date-selection="dateSelection"
        :custom-date="customDate"
        :initial-date="initialDate"
        :initial-site-ids="initialSiteIds"
        :dates-with-showing="datesWithShowing"
        :formatted-dates-with-showing="formattedDatesWithShowing"
        :has-filter="hasFilter"
        :use-class-filter="useClassFilter"
        :title-class-tabs="titleClassTabs"
        :selected-title-class-id="selectedTitleClassId"
        :showing-badge-filter-options="showingBadgeFilterOptions"
        :selected-showing-badge-id="selectedShowingBadgeId"
        :is-movie-page="isMoviePage"
        :is-mobile-view="isMobileView"
        :is-phone-preview="isPhonePreview"
        :is-showtimes-dark-mode="isShowtimesDarkMode"
        :use-mobile-register-layout="useMobileRegisterLayout"
        :allow-past-shows="allowPastShows"
        :all-movies="allMovies"
        :border-radius="buttonBorderRadius"
        :add-fixed-position="addFixedPosition"
        :top-margin="topMargin"
        :toggle-filter="toggleFilter"
        :center-align-date-filter="centerAlignDateFilter"
        :set-full-width-heading="setFullWidthHeading"
        :tab="tab"
        @update:date="value => date = value"
        @update:date-selection="value => dateSelection = value"
        @update:showtimes-display="value => showtimesDisplay = value"
        @update:selected-title-class-id="value => selectedTitleClassId = value"
        @update:tab="value => tab = value"
        @select-site="(siteId) => selectSingleSite(siteId)"
        @show-site-selector-drawer="$emit('show-site-selector-drawer')"
        @toggle-showing-badge="showingBadgeId => toggleShowingBadgeFilter(showingBadgeId)"
      />
      <movie-page-showtimes-heading
        v-else-if="$consumer && isMoviePage"
        :is-movie-page="isMoviePage"
        :is-mobile-view="isMobileView"
        :is-showtimes-dark-mode="isShowtimesDarkMode"
        :showtimes-date-picker-type="showtimesDatePickerType"
        :showtimes-allow-all="showtimesAllowAll"
        :date="date"
        :date-selection="dateSelection"
        :custom-date="customDate"
        :initial-date="initialDate"
        :initial-site-ids="initialSiteIds"
        :dates-with-showing="datesWithShowing"
        :formatted-dates-with-showing="formattedDatesWithShowing"
        :all-movies="allMovies"
        :allow-past-shows="allowPastShows"
        :border-radius="borderRadius"
        :display-dates-without-showings="displayDatesWithoutShowings"
        :center-align-date-filter="centerAlignDateFilter"
        :top-margin="topMargin"
        :show-date-picker="showDatePicker"
        :show-multiple-sites="showMultipleSites"
        :use-current-location="useCurrentLocation"
        :navigate-on-select="!showMultipleSites"
        :multiple-selection="enableMultiLocationFilter"
        @update:date="value => date = value"
        @update:date-selection="value => dateSelection = value"
        @update:show-calendar="value => showCalendar = value"
        @save-locations="(sitesIds, getClosestLocations) => selectMultipleSites(sitesIds, getClosestLocations)"
        @show-site-selector-drawer="$emit('show-site-selector-drawer')"
      />
      <register-showtimes-heading
        v-else
        :has-filter="hasFilter"
        :show-date-filter="showDateFilter"
        :showtimes-display-type-allow-toggle="showtimesDisplayTypeAllowToggle"
        :showtimes-date-picker-type="showtimesDatePickerType"
        :showtimes-allow-all="showtimesAllowAll"
        :date="date"
        :date-selection="dateSelection"
        :custom-date="customDate"
        :initial-date="initialDate"
        :initial-site-ids="initialSiteIds"
        :dates-with-showing="datesWithShowing"
        :formatted-dates-with-showing="formattedDatesWithShowing"
        :is-movie-page="isMoviePage"
        :is-mobile-view="isMobileView"
        :is-showtimes-dark-mode="isShowtimesDarkMode"
        :use-mobile-register-layout="useMobileRegisterLayout"
        :allow-past-shows="allowPastShows"
        :all-movies="allMovies"
        :border-radius="borderRadius"
        :selected-showing-badge-filter="selectedShowingBadgeFilter"
        :filtered-movies="filteredMovies"
        :search="search"
        :selected-movie="selectedMovie"
        :center-align-date-filter="centerAlignDateFilter"
        @update:date="value => date = value"
        @update:date-selection="value => dateSelection = value"
        @clear-showing-badge="selectedShowingBadgeId = null"
        @filter-movies="(val, update, abort) => filterMovies(val, update, abort)"
        @select-movie="movie => selectMovie(movie)"
        @clear-search="search = ''"
        @clear-movie="selectedMovie = null; search = ''"
      />

      <showtimes-by-film
        v-if="showShowtimesByFilm"
        :id="id"
        ref="showtimesList"
        :all-movies="allMovies"
        :allow-past-shows="allowPastShows"
        :banner-background="bannerBackground"
        :banner-brightness="bannerBrightness"
        :banner-opacity="bannerOpacity"
        :border-radius="borderRadius"
        :clean-image="cleanImage"
        :compact="showtimeView === SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM_COMPACT"
        :is-movie-page="isMoviePage"
        :movies-and-showings-by-date="moviesAndShowingsByDate"
        :reloading="reloading"
        :shadow="shadow"
        :shown="shown"
        :show-attendance-levels="showAttendanceLevels"
        :show-button-progress-bar="showButtonProgressBar"
        :show-button-progress-bar-in-grid-view="showButtonProgressBarInGridView"
        :show-class="showClass"
        :showings-by-date-and-movie="showingsByDateAndMovie"
        :showings-query="showingsForDate"
        :show-multiple-sites="showMultipleSites"
        :showtimes-grid-button-use-button-drawer="showtimesGridButtonUseButtonDrawer"
        :showtimes-list-button-type="showtimesListButtonType"
        :show-movie-image="showMovieImage"
        :show-movie-synopsis="showMovieSynopsis"
        :movie-synopsis-wrapped="movieSynopsisWrapped"
        :truncated-line-count="truncatedLineCount"
        :mobile-truncated-line-count="mobileTruncatedLineCount"
        :showtimes-grid-button-type="showtimesGridButtonType"
        :showtimes-blocks-background-color="showtimesBlocksBackgroundColor"
        :showtimes-blocks-background-color-hex="showtimesBlocksBackgroundColorHex"
        :showtimes-blocks-text-light="showtimesBlocksTextLight"
        :showtimes-view-type="showtimesViewType"
        :slider-controls-visible="sliderControlsVisible"
        :slider-pagination-visible="sliderPaginationVisible"
        :slider-autoplay="sliderAutoplay"
        :slider-paginate-grouped-slides="sliderPaginateGroupedSlides"
        :slider-delay-in-seconds="sliderDelayInSeconds"
        :slider-gutter="sliderGutter"
        :slider-per-view="sliderPerView"
        :grid-per-row="gridPerRow"
        :showtimes-display-type="showtimesDisplay || showtimesDisplayType"
        :is-showtimes-dark-mode="isShowtimesDarkMode"
        :solid-showing-badges="solidShowingBadges"
        :use-banner="useBanner"
        :show-date-headings="showDateHeadings"
        :show-site-headings="showSiteHeadings"
        :show-showing-badge="showShowingBadge"
        :show-trailer-button="showTrailerButton"
        :enable-leaving-soon-banner="enableLeavingSoonBanner"
        :next-showings-available="nextShowingsAvailable"
        :is-filtered="isFiltered"
        :is-phone-preview="isPhonePreview"
        :is-mobile-view="isMobileView"
        :button-type="buttonType"
        :button-color="buttonColor"
        :button-color-hex="buttonColorHex"
        :button-border-radius="buttonBorderRadius"
        :selected-locations-name="selectedLocationsName"
        :closest-locations-ids="closestLocationsIds"
        :use-current-location="useCurrentLocation"
        @common-showing-badges="commonShowingBadges => $emit('common-showing-badges', commonShowingBadges)"
        @select-movie="movie => selectMovie(movie)"
        @select-showing="showing => $emit('select-showing', showing)"
        @edit-held-seats="showtime => editHeldSeats(showtime)"
        @play-trailer="playTrailer"
        @toggle-showing-badge="showingBadgeId => toggleShowingBadgeFilter(showingBadgeId)"
        @view-next-showings="viewNextShowing"
        @clear-filters="clearFilters"
      />
      <showtimes-by-screen
        v-else-if="showtimeView === SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_CINEMA"
        :showings-by-screen="showingsByScreen"
        :showings-query="showingsForDate"
        :all-movies="allMovies"
        :banner-background="bannerBackground"
        :banner-brightness="bannerBrightness"
        :banner-opacity="bannerOpacity"
        :solid-showing-badges="solidShowingBadges"
        :show-attendance-levels="showAttendanceLevels"
        :allow-past-shows="allowPastShows"
        :reloading="reloading"
        @common-showing-badges="commonShowingBadges => $emit('common-showing-badges', commonShowingBadges)"
        @select-movie="movie => selectMovie(movie)"
        @select-showing="showing => $emit('select-showing', showing)"
        @edit-held-seats="showtime => editHeldSeats(showtime)"
      />
      <schedule-view
        v-else-if="showtimeView === SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_SCHEDULE && ($register && !useMobileRegisterLayout)"
        class="absolute-full"
        :fit-height="true"
        style="top: 58px; bottom: 3px;"
        :date="date"
        :shown="shown"
        save-label="Save Page For Register Type"
        :can-save="currentUser.admin"
        :showing-query="showingsForDate"
        :screen-ids="ticketViewPage.config.screenIds"
        :show-time-header="ticketViewPage.config.showTimeHeader"
        :show-time-slot-header="ticketViewPage.config.showTimeSlotHeader"
        :show-screen-labels="ticketViewPage.config.showScreenLabels"
        :show-times="ticketViewPage.config.showTimes"
        :show-time-chips="ticketViewPage.config.showTimeChips"
        :show-rating="ticketViewPage.config.showRating"
        :show-seat-chart="ticketViewPage.config.showSeatChart"
        :show-price-card="ticketViewPage.config.showPriceCard"
        :show-showing-badges="ticketViewPage.config.showShowingBadges"
        :show-posters="ticketViewPage.config.showPosters"
        :show-progress-bar="ticketViewPage.config.showProgressBar"
        :highlight-starting-showings="ticketViewPage.config.highlightStartingShowings"
        :highlight-ending-showings="ticketViewPage.config.highlightEndingShowings"
        :highlight-current-showings="ticketViewPage.config.highlightCurrentShowings"
        :show-relative-times="ticketViewPage.config.showRelativeTimes"
        :show-relative-time-chips="ticketViewPage.config.showRelativeTimeChips"
        :show-current-time="ticketViewPage.config.showCurrentTime"
        :hide-distance-showings="ticketViewPage.config.hideDistanceShowings"
        :distant-showing-threshold-hours="ticketViewPage.config.distantShowingThresholdHours"
        :zoom="ticketViewPage.config.zoom"
        @edit-held-seats="showtime => editHeldSeats(showtime)"
        @save="config => ticketViewPage.saveConfig(config)"

        @select-showing="(showing) => $emit('select-showing', showing)"
      />
    </div>
    <held-seat-dialog
      ref="heldSeatDialog"
      @select-showing="showing => $emit('select-showing', showing)"
      @select-order="order => $emit('select-order', order)"
      @request-authorization="callback => $emit('request-authorization', callback)"
    />
    <trailer-player-dialog
      ref="trailerDialog"
      :trailer-youtube-id="trailerYoutubeId"
      :is-phone-preview="isPhonePreview"
    />
  </div>
</template>

<script>

import { date } from 'quasar'
import { defineAsyncComponent } from 'vue'
const { formatDate, extractDate } = date
import { TEXT_ALIGN_OPTIONS } from 'src/models/carousel/Carousel'
import { BUTTON_TYPES, HEADING_TYPES, SHOWTIMES_BUTTON_TYPES, SHOWTIMES_DATE_PICKER_TYPES, SHOWTIMES_VIEW_TYPES, SITE_INFO_DISPLAY_TYPES } from 'src/models/page/Section'
import { HEADER_TYPES, SHOWTIME_LIST_VIEW_POS_OPTIONS } from 'src/models/site/Site'

export default {
  name: 'ShowtimesList',
  components: {
    ShowtimesByFilm: defineAsyncComponent(() => import('src/modules/consumer/components/ShowtimesByFilm')),
    ShowtimesByScreen: defineAsyncComponent(() => import('src/modules/consumer/components/ShowtimesByScreen')),
    HeldSeatDialog: defineAsyncComponent(() => import('src/modules/consumer/components/HeldSeatDialog')),
    ScheduleView: defineAsyncComponent(() => import('src/modules/admin/report/ScheduleView')),
    TrailerPlayerDialog: defineAsyncComponent(() => import('src/modules/consumer/components/TrailerPlayerDialog')),
    SectionShowtimesHeading: defineAsyncComponent(() => import('src/modules/consumer/components/showtimes-list/SectionShowtimesHeading')),
    RegisterShowtimesHeading: defineAsyncComponent(() => import('src/modules/consumer/components/showtimes-list/RegisterShowtimesHeading')),
    MoviePageShowtimesHeading: defineAsyncComponent(() => import('src/modules/consumer/components/showtimes-list/MoviePageShowtimesHeading'))
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite', 'titleClasses', 'showingBadges', 'sites'],
  props: {
    id: {
      type: Number,
      required: false,
      default: 0
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    showTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    headingColor: {
      type: String,
      default: 'primary',
      required: false
    },
    headingColorHex: {
      type: String,
      default: '#FFFFFF',
      required: false
    },
    headingAlign: {
      type: String,
      default: null,
      required: false
    },
    headingFontSize: {
      type: String,
      default: null,
      required: false
    },
    headingType: {
      type: String,
      default: 'default',
      required: false
    },
    headingBackgroundImageUrl: {
      type: String,
      default: null,
      required: false
    },
    headingBackgroundImageRepeat: {
      type: Boolean,
      required: false,
      default: true
    },
    shown: {
      type: Boolean,
      required: false,
      default: true
    },
    initialDate: {
      type: String,
      required: false,
      default: null
    },
    showDateFilter: {
      type: Boolean,
      required: false,
      default: true
    },
    showtimesSiteSelectorTypes: {
      type: Array,
      required: false,
      default: null
    },
    showDateHeading: {
      type: Boolean,
      required: false,
      default: false
    },
    showShowingBadge: {
      type: Boolean,
      required: false,
      default: true
    },
    titleClassId: {
      type: Number,
      required: false,
      default: null
    },
    showingByScreen: {
      type: Boolean,
      required: false,
      default: true
    },
    initialMovieId: {
      type: String,
      required: false,
      default: null
    },
    allowPastShows: {
      type: Boolean,
      default: false
    },
    bannerBackground: {
      type: Boolean,
      required: false,
      default: true
    },
    bannerBrightness: {
      type: Number,
      required: false,
      default: 0.3
    },
    bannerOpacity: {
      type: Number,
      required: false,
      default: 0.8
    },
    solidShowingBadges: {
      type: Boolean,
      required: false,
      default: false
    },
    showAttendanceLevels: {
      type: Boolean,
      required: false,
      default: true
    },
    isMoviePage: {
      type: Boolean,
      required: false,
      default: false
    },
    showButtonProgressBar: {
      type: Boolean,
      required: false,
      default: false
    },
    showButtonProgressBarInGridView: {
      type: Boolean,
      required: false,
      default: false
    },
    overrideShowtimeView: {
      type: String,
      required: false,
      default: null
    },
    initialMovieIds: {
      type: Array,
      required: false,
      default: () => ([])
    },
    initialShowingIds: {
      type: Array,
      required: false,
      default: () => ([])
    },
    initialTitleClassIds: {
      type: Array,
      required: false,
      default: null
    },
    initialSiteIds: {
      type: Array,
      required: false,
      default: null
    },
    showtimesDatePickerType: {
      type: String,
      required: false,
      default: SHOWTIMES_DATE_PICKER_TYPES.TOGGLE
    },
    showtimesViewType: {
      type: String,
      required: false,
      default: SHOWTIMES_VIEW_TYPES.BLOCK
    },
    sliderControlsVisible: {
      type: Boolean,
      required: false,
      default: true
    },
    sliderPaginationVisible: {
      type: Boolean,
      required: false,
      default: true
    },
    sliderAutoplay: {
      type: Boolean,
      required: false,
      default: false
    },
    sliderPaginateGroupedSlides: {
      type: Boolean,
      required: false,
      default: false
    },
    sliderDelayInSeconds: {
      type: Number,
      required: false,
      default: 6
    },
    sliderGutter: {
      type: Number,
      required: false,
      default: 4
    },
    showtimesDisplayType: {
      type: String,
      required: false,
      default: 'list'
    },
    showtimesDisplayTypeAllowToggle: {
      type: Boolean,
      required: false,
      default: false
    },
    showtimesListButtonType: {
      type: String,
      required: false,
      default: SHOWTIMES_BUTTON_TYPES.BASIC
    },
    showMovieImage: {
      type: Boolean,
      required: false,
      default: true
    },
    showMovieSynopsis: {
      type: Boolean,
      required: false,
      default: true
    },
    movieSynopsisWrapped: {
      type: Boolean,
      required: false,
      default: true
    },
    truncatedLineCount: {
      type: Number,
      required: false,
      default: 9
    },
    mobileTruncatedLineCount: {
      type: Number,
      required: false,
      default: 3
    },
    showtimesGridButtonType: {
      type: String,
      required: false,
      default: SHOWTIMES_BUTTON_TYPES.COMPACT
    },
    showtimesGridButtonUseButtonDrawer: {
      type: Boolean,
      required: false,
      default: false
    },
    showtimesBlocksBackgroundColor: {
      type: String,
      required: false,
      default: null
    },
    showtimesBlocksBackgroundColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    showtimesBlocksTextLight: {
      type: Boolean,
      required: false,
      default: false
    },
    isShowtimesDarkMode: {
      type: Boolean,
      required: false,
      default: false
    },
    showtimesAllowAll: {
      type: Boolean,
      required: false,
      default: false
    },
    useBanner: {
      type: Boolean,
      required: false,
      default: false
    },
    cleanImage: {
      type: Boolean,
      required: false,
      default: false
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 0
    },
    shadow: {
      type: Number,
      required: false,
      default: 0
    },
    showClass: {
      type: Boolean,
      required: false,
      default: false
    },
    useClassFilter: {
      type: Boolean,
      required: false,
      default: false
    },
    showTrailerButton: {
      type: Boolean,
      required: false,
      default: false
    },
    useShowingBadgeFilter: {
      type: Boolean,
      required: false,
      default: false
    },
    matchAllShowingBadges: {
      type: Boolean,
      required: false,
      default: true
    },
    filterShowingBadgeIds: {
      type: Array,
      required: false,
      default: null
    },
    filterOptionShowingBadgeIds: {
      type: Array,
      required: false,
      default: null
    },
    ticketViewPage: {
      type: Object,
      required: false,
      default: null
    },
    enableLeavingSoonBanner: {
      type: Boolean,
      required: false,
      default: false
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    gridPerRow: {
      type: Number,
      required: false,
      default: 4
    },
    sliderPerView: {
      type: Number,
      required: false,
      default: 3
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.UNELEVATED
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    buttonBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    useMobileRegisterLayout: {
      type: Boolean,
      required: false,
      default: false
    },
    displayDatesWithoutShowings: {
      type: Boolean,
      required: false,
      default: false
    },
    useCurrentLocation: {
      type: Boolean,
      required: false,
      default: false
    },
    closestLocationsIds: {
      type: Array,
      required: false,
      default: () => []
    },
    flattenZoneGroups: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['show-site-selector-drawer', 'common-showing-badges', 'select-showing', 'select-order', 'request-authorization', 'showtimes-loading', 'select-movie', 'select-site', 'select-sites'],
  data () {
    return {
      today: this.currentSite.today,
      tomorrow: this.currentSite.tomorrow,
      errorOccurred: false,
      dateSelection: 'all',
      date: (this.initialDate || this.today),
      customDate: null,
      search: '',
      selectedMovie: null,
      movieId: this.initialMovieId,
      reloading: false,
      titleClassTabs: null,
      selectedTitleClassId: null,
      showtimesDisplay: null,
      trailer: false,
      trailerYoutubeId: null,
      showtimesVisible: false,
      selectedShowingBadgeId: null,
      showSiteSelector: false,
      toggleFilter: true,
      addFixedPosition: false,
      showtimesLoaded: false,
      showtimeSelectedDate: this.date,
      tab: null,
      initialDateLoaded: false,
      scrollUp: false,
      showDatePicker: false,
      showSiteFilterDialog: false,
      filteredLocationsIds: [],
      showCalendar: false,
      selectedLocationsName: null,
      getClosestLocations: false
    }
  },

  computed: {
    allMovies () {
      return !this.movieId
    },
    showtimeView () {
      if (this.overrideShowtimeView) return this.overrideShowtimeView

      if (this.movieId || !(this.$register && !this.useMobileRegisterLayout) || !this.currentUser?.posShowtimeViewPreference) return SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM

      if (this.currentUser.overridePosShowtimeViewPreference) return this.currentUser.posShowtimeViewPreference

      return this.currentSite.defaultPosShowtimeViewPreference
    },
    currentShowtimes () {
      return _(this.showingsForDate.data)
        .filter(s => {
          if (this.$register && this.allowPastShows) return true

          if (this.$register && this.currentSite.showPastShowingsInPos) return true

          if (this.$consumer && this.currentSite.showPastShowingsOnline) return !s.beforeCutoffTime

          if (this.$consumer) {
            if (this.currentSite.showPastShowingsOnline) return !s.beforeCutoffTime
          }

          return !s.localPast
        })
    },
    moviesAndShowingsByDate () {
      // Group Showings by Movie
      // Sort Showings within Movies by their showtime (earlier showings display first under a Movie)
      // Sort Movies by their magic sort value
      return _(this.currentShowtimes)
        .groupBy('movie.id')
        .map(showings => {
          _.each(showings, showing => { showing.site = this.currentSite })

          const showingsGroupedByDate = _(showings)
            .groupBy(showing => formatDate(showing.siteTime, 'YYYY-MM-DD'))
            .value()

          const sortedShowingsGroupedByDate = {}
          // Sort Showings within a Movie by showtime and group by movie.id within each date
          for (const date in showingsGroupedByDate) {
            const showingsForDate = showingsGroupedByDate[date]
            const groupedByMovieId = _(showingsForDate)
              .groupBy('movie.id')
              .mapValues(movieShowings =>
                _.orderBy(movieShowings, ['time', 'screen.displayOrder'], ['asc', 'asc'])
              )
              .value()

            // Flatten the grouped structure back to an array for compatibility
            sortedShowingsGroupedByDate[date] = _(groupedByMovieId)
              .values()
              .flatten()
              .value()
          }

          return {
            movie: showings[0].movie,
            showings: sortedShowingsGroupedByDate,
            allShowings: showings
          }
        })
        // Sort by movie magic sort value when date is unset
        // Sort by movie day of magic sort value when date is set (ignores prioritisation elements that act across days)
        .sortBy(({ showings }) => {
          const firstShowing = _.values(showings)[0]?.[0]
          return !this.date
            ? firstShowing?.movie?.displayMetaData?.magicSortValue
            : firstShowing?.movie?.dayOfMagicSortValue
        })
        .value()
    },
    showingsByDateAndMovie () {
      const showingsGroupedByDate = this.currentShowtimes.groupBy(showing => {
        return formatDate(showing.movieSiteTime, 'YYYY-MM-DD') // when movie is shown at a different site, the siteTime is the time in the site timezone
      }).value()
      const showingsGroupedByDateAndMovie = {}

      for (const date in showingsGroupedByDate) {
        showingsGroupedByDateAndMovie[date] =
          _(showingsGroupedByDate[date])
            .groupBy('movie.id')
            .map(showings => {
              _.each(showings, showing => { showing.site = this.currentSite })
              return {
                movie: showings[0].movie,
                showings: _(showings).sortBy(o => o.screen.displayOrder).sortBy('time').value() // Sort first by time, then display order
              }
            })
            .sortBy(({ showings }) => showings[0].movie.displayMetaData?.magicSortValue) // Sort by magic movie value
            .value()
      }

      return showingsGroupedByDateAndMovie
    },
    showingsByScreen () {
      return this.currentShowtimes
        .groupBy('screen.displayOrder')
        .map(showings => {
          _.each(showings, showing => { showing.site = this.currentSite })
          return {
            screen: showings[0].screen,
            showings: _(showings).sortBy('time').value() // Sort first by time
          }
        })
        .sortBy(({ showings, screen }) => screen.displayOrder) // Sort by screen display order
        .value()
    },
    formattedDatesWithShowing () {
      if (!this.datesWithShowing.value || !_.isArray(this.datesWithShowing.value)) return []

      return this.datesWithShowing.value.map(date => date.replace(/-/g, '/'))
    },
    filteredMovies () {
      if (this.search.length === 0) return this.currentAndUpcomingMovies.data
      const searchTerms = this.search.toLowerCase().split(' ')
      const searchFields = ['name', 'synopsis', 'starring', 'genre', 'dateOfFirstShowing']
      return this.currentAndUpcomingMovies.data.filter(movie => {
        return _.every(searchTerms, term => {
          return _.some(searchFields, field => {
            if (!movie[field]) return false

            if (field === 'dateOfFirstShowing') {
              return this.formatDate(movie[field], 'MMMM YYYY dddd D').toLowerCase().includes(term)
            } else {
              return movie[field].toLowerCase().includes(term)
            }
          })
        })
      })
    },
    customDateFormatted () {
      return formatDate(extractDate(this.date + ' 00:00', 'YYYY-MM-DD HH:mm'), 'MMM DD')
    },
    selectedShowingBadgeFilter () {
      if (!this.selectedShowingBadgeId) return null
      return _.find(this.showingBadges.data, { id: this.selectedShowingBadgeId })
    },
    showingBadgeFilterOptions () {
      if (this.matchAllShowingBadges && this.filterOptionShowingBadgeIds?.length) return []

      if (!this.filterOptionShowingBadgeIds?.length && !this.selectedShowingBadgeId) return []

      return _(this.filterOptionShowingBadgeIds || []).concat([this.selectedShowingBadgeId])
        .compact()
        .uniq()
        .map(id => _.find(this.showingBadges.data, { id }))
        .compact()
        .value()
    },
    showDateHeadings () {
      return this.isMoviePage || this.showDateHeading ||
      (this.showtimesDatePickerType === SHOWTIMES_DATE_PICKER_TYPES.TOGGLE && this.showtimesAllowAll && this.dateSelection === 'all')
    },
    showSiteHeadings () {
      return this.showMultipleSites && this.isMoviePage
    },
    enableMultiLocationFilter () {
      // This is for cinema chain who need show movie in their circuit level website
      return this.currentSite.consumerConfig.showSiteSelectorOnMoviePage && this.$consumer && this.showMultipleSites
    },
    hasFilter () {
      if (this.$kiosk || (this.$register && !this.useMobileRegisterLayout)) return false
      return _.includes(this.showtimesSiteSelectorTypes, SITE_INFO_DISPLAY_TYPES.BANNER) || (this.useClassFilter && !_.isEmpty(this.titleClassTabs)) || !_.isEmpty(this.showingBadgeFilterOptions)
    },
    nextShowingsAvailable () {
      if (_.isEmpty(this.formattedDatesWithShowing)) return null

      const nextShowingDateString = _(this.formattedDatesWithShowing)
        .map(date => date.replace(/\//g, '-'))
        .find(date => date !== this.today)

      if (_.isNil(nextShowingDateString)) return null

      // Create a proper Date object with timezone information to avoid timezone offset issues
      const nextShowingDate = new Date(nextShowingDateString + 'T00:00:00')

      return `${this.$t('showing.nextAvailableShowing')}: ${this.$d(nextShowingDate, 'mediumLongDate')}`
    },
    isFiltered () {
      return ((this.useClassFilter && this.titleClassTabs?.length > 1 && this.selectedTitleClassId !== null) || (this.showingBadgeFilterOptions.length > 0 && this.selectedShowingBadgeId !== null))
    },
    topMargin () {
      if (this.currentSite.consumerConfig.stickyHeader || this.scrollUp) {
        if (window._capacitorNative || this.$capacitor?.isCapacitor) {
          if (this.headerShowTopBar) return '178px'
          return '130px'
        } else {
          if (this.headerShowTopBar) return '118px'
          return '70px'
        }
      } else {
        if (window._capacitorNative || this.$capacitor?.isCapacitor) {
          return '60px'
        } else {
          return '0px'
        }
      }
    },
    headerShowTopBar () {
      return this.currentSite.consumerConfig.headerType !== HEADER_TYPES.DEFAULT && (this.currentSite.consumerConfig.showSiteSelectorOnNavigations || this.currentCircuit.supportsTranslations)
    },
    textColor () {
      if (this.addFixedPosition) return this.$isColorDark(this.currentSite.backgroundColor) ? 'white' : 'black'
      if (this.isShowtimesDarkMode) return 'white'
      if (this.toggleFilter) return 'primary'
      return 'dark'
    },
    configureDarkMode () {
      if (this.addFixedPosition) return this.$isColorDark(this.currentSite.backgroundColor)
      return this.isShowtimesDarkMode
    },
    setFullWidthHeading () {
      return (this.headingType !== HEADING_TYPES.DEFAULT || (this.headingType === HEADING_TYPES.DEFAULT && this.headingAlign === TEXT_ALIGN_OPTIONS.CENTER)) && this.showTitle
    },
    centerAlignDateFilter () {
      return (!this.showTitle && !_.includes(this.showtimesSiteSelectorTypes, SITE_INFO_DISPLAY_TYPES.DROPDOWN)) || this.setFullWidthHeading || this.showtimesDatePickerType === SHOWTIMES_DATE_PICKER_TYPES.SWIPER
    },
    showMultipleSites () {
      return this.currentSite.consumerConfig.showMoviesAndShowingsForMultipleSites && this.$consumer
    },
    showShowtimesByFilm () {
      if (this.showMultipleSites && (_.isEmpty(this.initialSiteIds) || _.isNil(this.initialSiteIds))) return false

      return (this.showtimeView === SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM || this.showtimeView === SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM_COMPACT)
    }
  },
  watch: {
    initialMovieId () {
      this.movieId = this.initialMovieId
    },
    dateSelection () {
      if (this.dateSelection === 'today') {
        this.date = this.today
        this.$trackEvent(this.$customerEvents.SHOWTIMES_SELECTED_TODAY)
      } else if (this.dateSelection === 'tomorrow') {
        this.date = this.tomorrow
        this.$trackEvent(this.$customerEvents.SHOWTIMES_SELECTED_TOMORROW)
      } else if (this.dateSelection === 'custom') {
        this.validateDateSelection()
        this.$trackEvent(this.$customerEvents.SHOWTIMES_SELECTED_CUSTOM)
      } else if (this.dateSelection === 'all') {
        this.date = null
        this.validateDateSelection()
        this.$trackEvent(this.$customerEvents.SHOWTIMES_SELECTED_ALL)
      }
    },
    initialSiteIds: {
      handler (oldVal, newVal) {
        // Only reset date if site IDs actually changed and are different
        if (!_.isEqual(oldVal, newVal)) {
          this.date = this.initialDate || this.today
          // Trigger date validation after reset
          this.$nextTick(() => {
            this.validateDateSelection()
          })
        }
      },
      immediate: true,
      deep: true
    },
    'datesWithShowing.value': {
      handler () {
        if (!!this.date && this.allowPastShows) {
          this.initialDateLoaded = true
          return
        }

        if (!this.datesWithShowing.loaded) return

        if (!this.datesWithShowing.value?.length) {
          this.initialDateLoaded = true
          return
        }

        if (this.date && _.includes(this.datesWithShowing.value, this.date)) {
          this.initialDateLoaded = true
          return
        }

        const initialDate = this.getAndResetInitialDate()
        if (initialDate && _.includes(this.datesWithShowing.value, initialDate)) {
          this.date = initialDate
        } else {
          if ((this.allMovies || this.showMultipleSites || this.$kiosk) && (!this.showtimesAllowAll || !this.showDateFilter || this.showtimesDatePickerType === SHOWTIMES_DATE_PICKER_TYPES.SELECTOR)) {
            this.date = this.initialDate || this.today
          } else {
            this.date = null
          }
        }

        this.validateDateSelection()
        this.initialDateLoaded = true
      },
      immediate: true
    },
    date: {
      handler () {
        if (!this.initialTitleClassIds) {
          this.selectedTitleClassId = null
          this.titleClassTabs = null
        }

        this.validateDateSelection()
      },
      immediate: true
    },
    'showingsForDate.loading' (val) {
      this.$emit('showtimes-loading', val)
    },
    currentShowtimes () {
      if (this.titleClassTabs || !this.currentShowtimes.value() || !this.currentShowtimes.value().length) return

      if (this.initialTitleClassIds) {
        this.titleClassTabs = _(this.initialTitleClassIds).map(titleClassId => _.find(this.titleClasses.data, ({ id }) => id === titleClassId)).compact().value()
      } else {
        this.titleClassTabs = this.currentShowtimes.map(showing => showing.movie.titleClass).uniq().without(null).sortBy('displayOrder').compact().value()
      }
    },
    showtimesDisplayType: {
      handler: function () {
        this.showtimesDisplay = this.showtimesDisplayTypeAllowToggle ? this.showtimesDisplayType : null
      },
      immediate: true
    },
    showtimesDisplayTypeAllowToggle: {
      handler: function () {
        this.showtimesDisplay = this.showtimesDisplayTypeAllowToggle ? this.showtimesDisplayType : null
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, {
      formatDate,
      SHOWTIMES_DATE_PICKER_TYPES,
      SHOWTIME_LIST_VIEW_POS_OPTIONS,
      SITE_INFO_DISPLAY_TYPES,
      HEADING_TYPES,
      TEXT_ALIGN_OPTIONS,
      BUTTON_TYPES,
      HEADER_TYPES
    })
    if (!this.showingsForDate?.options?.skip) {
      if ((this.showMultipleSites && this.dateSelection === 'all') || this.$kiosk) this.showToday()
      this.reset()
    }
    this.fetchShowings()
  },
  unmounted () {
    this.isDestroyed = true

    if (this.fetchShowingsTimeout) {
      clearTimeout(this.fetchShowingsTimeout)
    }
    document.removeEventListener('scroll', this.checkScrolledHeight)
  },
  methods: {
    async reset () {
      this.validateToday()
      this.reloading = true
      await this.datesWithShowing.fetch(true)
      await this.showingsForDate.fetch(true)
      this.reloading = false
    },
    showToday () {
      this.dateSelection = 'today'
      this.customDate = false
    },
    fetchShowings () {
      // Set timeout instead of set interval, prevents call
      // stacking if fetch takes time
      this.fetchShowingsTimeout = setTimeout(async () => {
        if (this.isDestroyed) return
        if (this.showingsForDate.loading) return

        this.validateToday()
        this.reloading = true
        if (!this.datesWithShowing?.options?.skip) {
          await this.datesWithShowing.fetch(true)
        }
        if (!this.showingsForDate?.options?.skip) {
          await this.showingsForDate.fetch(true)
        }
        this.reloading = false

        // Call recursively
        this.fetchShowings()
      }, 20 * 1000)
    },
    validateDateSelection () {
      if (this.date === this.today) {
        this.dateSelection = 'today'
        this.customDate = false
      } else if (this.date === this.tomorrow) {
        this.dateSelection = 'tomorrow'
        this.customDate = false
      } else if (this.date === null) {
        this.dateSelection = 'all'
        this.customDate = false
      } else {
        this.dateSelection = 'custom'
        this.customDate = true
      }
    },

    filterMovies (val, update, abort) {
      update(() => {
        this.search = val
      })
    },
    selectMovie (movie) {
      this.$emit('select-movie', movie)
    },
    validateToday () {
      if (this.today === this.currentSite.today) return

      const oldToday = this.today
      this.today = this.currentSite.today
      this.tomorrow = this.currentSite.tomorrow

      if (this.date === oldToday) {
        this.date = this.today
      }

      this.validateDateSelection()
    },
    editHeldSeats (showtime) {
      this.$refs.heldSeatDialog.show(showtime)
    },

    getAndResetInitialDate () {
      let initialDate = null
      if (this.$route.query.date) {
      // Capture the data
        const dateStr = this.$route.query.date
        const maybeDate = new Date(dateStr)
        if (maybeDate.toDateString() !== 'Invalid Date' && maybeDate.getTime() >= (new Date(this.today)).getTime()) {
          initialDate = dateStr
        }
        // Remove the date from the route
        history.replaceState({}, null, '#' + this.$route.path)
      }

      return initialDate
    },
    toggleDisplay () {
      this.showtimesDisplay = this.showtimesDisplay === 'list' ? 'grid' : 'list'
    },
    playTrailer (playTrailer) {
      if (playTrailer == null) return
      this.trailerYoutubeId = playTrailer
      this.$refs.trailerDialog.show()
    },
    toggleShowingBadgeFilter (showingBadgeId) {
      this.selectedShowingBadgeId = (this.selectedShowingBadgeId === showingBadgeId ? null : showingBadgeId)
    },
    viewNextShowing () {
      // this is because q-date expect date format with - while date-selector expect date format with /
      const nextAvailableShowingDates = this.showtimesDatePickerType === SHOWTIMES_DATE_PICKER_TYPES.TOGGLE ? this.formattedDatesWithShowing.map(date => date.replace(/\//g, '-')) : this.formattedDatesWithShowing
      if (this.datesWithShowing.value.includes(this.today)) {
        this.date = nextAvailableShowingDates[1]
      } else {
        this.date = nextAvailableShowingDates[0]
      }
    },
    clearFilters () {
      if (this.useClassFilter && this.titleClassTabs?.length > 1) this.selectedTitleClassId = null
      if (this.showingBadgeFilterOptions.length > 0) this.selectedShowingBadgeId = null
    },
    scrollHandler (value) {
      if (value.direction === 'up') {
        this.scrollUp = true
      } else {
        this.scrollUp = false
      }

      // Safety check: ensure ref exists before accessing $el
      if (!this.$refs.showtimesList || !this.$refs.showtimesList.$el) {
        return
      }

      const containerScroll = this.$refs.showtimesList.$el
      const containerRect = containerScroll.getBoundingClientRect()
      const containerScrollOffsetTop = containerRect.top + value.position.top
      const containerScrollOffsetBottom = containerRect.bottom + value.position.top

      const windowScroll = value.position.top
      const removeFixedPositionOffsetBottom = containerScrollOffsetBottom - 80
      // showtime widget show fixed position date picker
      // Check if window scroll has reached the top of the container &&   If the scroll is past the bottom offset, remove the fixed position
      if (windowScroll >= containerScrollOffsetTop && windowScroll <= removeFixedPositionOffsetBottom) {
        this.addFixedPosition = true
      } else {
        this.addFixedPosition = false
      }
      // movie page show date picker
      this.showDatePicker = !_.isEmpty(this.moviesAndShowingsByDate) && (windowScroll > 50 || this.showCalendar)
    },
    // movie page location multiple selection
    selectMultipleSites (siteIds, getClosestLocations) {
      this.getClosestLocations = getClosestLocations
      this.getSelectedLocationsName(siteIds)

      this.$emit('select-sites', siteIds, getClosestLocations)
    },
    // section component location single selection
    selectSingleSite (siteId) {
      if (!siteId) {
        console.warn('selectSingleSite: No site ID provided')
        return
      }
      this.getSelectedLocationsName([siteId])
      this.$emit('select-site', siteId)
    },
    getSelectedLocationsName (locations) {
      if (_.isEmpty(locations)) {
        this.selectedLocationsName = ''
        return
      }

      this.selectedLocationsName = (() => {
        const filteredLocations = _.filter(this.sites.data, site => _.includes(locations, site.id))
        const names = _.map(filteredLocations, 'name').filter(Boolean) // Remove any null/undefined names

        if (_.isEmpty(names)) {
          return ''
        }

        const shown = this.$q.screen.lt.sm ? names.slice(0, 1) : names.slice(0, 3)
        const remaining = names.length - shown.length

        return remaining > 0
          ? `${shown.join(', ')}, (${this.$tc('siteLocation.countMore', remaining)})`
          : shown.join(', ')
      })()
    }

  },
  models: {
    datesWithShowing: {
      variables () {
        return {
          ids: this.initialShowingIds ? this.initialShowingIds : null,
          movieId: this.movieId ? this.movieId : null,
          movieIds: this.initialMovieIds ? this.initialMovieIds : null,
          titleClassId: this.selectedTitleClassId ? this.selectedTitleClassId : null,
          titleClassIds: this.initialTitleClassIds ? this.initialTitleClassIds : null,
          siteIds: this.initialSiteIds || null,
          anyShowingBadgeIds: (!this.matchAllShowingBadges && this.filterShowingBadgeIds?.length) ? this.filterShowingBadgeIds : null,
          everyShowingBadgeIds: (this.matchAllShowingBadges && this.filterShowingBadgeIds?.length) ? this.filterShowingBadgeIds : [this.selectedShowingBadgeId],
          skip: !this.currentSite
        }
      },
      immediate: true
    },
    showingsForDate: {
      movie: 'default',
      showing: {
        movie: 'default',
        screen: 'default',
        seatsRemaining: true,
        seatsRemainingWithoutSocialDistancing: true
      },
      showings: {
        movie: 'default',
        screen: 'default',
        seatsRemaining: true,
        seatsRemainingWithoutSocialDistancing: true
      },
      screen: 'default',
      showingBadges: 'default',
      priceCard: 'default',
      predictedAttendance: true,
      seatsRemaining: true,
      seatsRemainingWithoutSocialDistancing: true,
      titleClass: 'default',
      variables () {
        return {
          date: this.date,
          ids: this.initialShowingIds ? this.initialShowingIds : null,
          movieId: this.movieId ? this.movieId : null,
          movieIds: this.initialMovieIds ? this.initialMovieIds : null,
          titleClassId: this.selectedTitleClassId ? this.selectedTitleClassId : null,
          titleClassIds: this.initialTitleClassIds ? this.initialTitleClassIds : null,
          siteIds: this.initialSiteIds || null,
          anyShowingBadgeIds: (!this.matchAllShowingBadges && this.filterShowingBadgeIds?.length) ? this.filterShowingBadgeIds : null,
          everyShowingBadgeIds: (this.matchAllShowingBadges && this.filterShowingBadgeIds?.length) ? this.filterShowingBadgeIds : [this.selectedShowingBadgeId],
          skip: !this.datesWithShowing.loaded || !this.initialDateLoaded || !this.currentSite
        }
      }
    },
    currentAndUpcomingMovies: 'default',
    movie: {
      datesWithShowing: 'default',
      datesWithPublicShowing: 'default',
      variables () {
        return { id: this.movieId && this.movieId }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.heading {
  margin-left: auto;
  margin-right: auto;
}

.text-h2 {
  // background: rgba(33,33,33,.4);
  min-width: unset !important;
  padding: 16px 8px 16px 0;

  color: var(--q-primary);
  font-size: 32px;
  line-height: 1.5rem;

}
.body--light .border-responsive-grey-3{
  border: 3px solid $grey-4;
}
.body--dark .border-responsive-grey-3{
  border: 3px solid rgba(255, 255, 255, 0.4);
}

.times-visible-detector {
  height: 0;
  position: relative;
  top: 85px;
}
.arrow-up {
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid lightgray;
  position: absolute;
  top: -8px;
}
.sticky-fixed-bar {
  top:0;
  background-color: var(--q-background);
  z-index: 1000;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  animation: goDown 0.3s ease-in-out forwards;
}

@keyframes goDown {
  0%{
    transform: translateY(-50px);
  }
  100% {
    transform: translateY(0px);
  }
}
</style>
